// /client/src/pages/Admin/DashboardAdherents/CommunityStatisticsTable.jsx
import React, { useEffect, useMemo, useState } from "react";
import { FaFilter, FaLayerGroup, FaTable } from "react-icons/fa";
import regrouperParTranchesDages from "./../../../utils/tranchesDages";
import { getCommunityStats } from "../../../services/dashboardServices";
import { getSettings } from "../../../services/settingsServices";

const TRANCHES_ORDER = [
  "0-3",
  "4-5",
  "6-11",
  "12-17",
  "18-25",
  "26-45",
  "46-59",
  "60-74",
  "75-100",
];
const ALL = "_ALL";

function Skeleton() {
  return (
    <div className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur p-5">
      <div className="h-5 w-64 bg-slate-200 dark:bg-slate-800 rounded animate-pulse mb-4" />
      <div className="h-10 w-full bg-slate-100 dark:bg-slate-800 rounded animate-pulse mb-4" />
      <div className="h-72 w-full bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
    </div>
  );
}

function Chip({ label, value, tone = "slate" }) {
  const tones = {
    emerald:
      "bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:ring-emerald-800/50",
    sky: "bg-sky-100 text-sky-800 ring-sky-200 dark:bg-sky-900/40 dark:text-sky-200 dark:ring-sky-800/50",
    slate:
      "bg-slate-100 text-slate-800 ring-slate-200 dark:bg-slate-900/40 dark:text-slate-200 dark:ring-slate-800/50",
    fuchsia:
      "bg-fuchsia-100 text-fuchsia-800 ring-fuchsia-200 dark:bg-fuchsia-900/40 dark:text-fuchsia-200 dark:ring-fuchsia-800/50",
  };
  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm ring-1 ${tones[tone]}`}
    >
      <span className="font-medium">{label}</span>
      <span className="font-bold">{value}</span>
    </div>
  );
}

export default function CommunityStatisticsTable() {
  const [raw, setRaw] = useState([]); // [{ commune, cp, genre, count, age }, ...]
  const [grouped, setGrouped] = useState({}); // output de regrouperParTranchesDages
  const [communes, setCommunes] = useState([]); // uniques
  const [selectedCommune, setSelectedCommune] = useState(ALL);
  const [selectedGenre, setSelectedGenre] = useState(ALL); // ALL | Masculin | Féminin
  const [selectedTranche, setSelectedTranche] = useState(ALL);
  const [mode, setMode] = useState("synthese"); // "synthese" | "detail"
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const [communityStats, settings] = await Promise.all([
          getCommunityStats(), // [{ commune, cp, genre, count, age }, ...]
          getSettings(),
        ]);
        if (!alive) return;

        // communes uniques
        const list = Array.from(
          new Set((communityStats || []).map((x) => x.commune).filter(Boolean))
        ).sort((a, b) => String(a).localeCompare(String(b), "fr"));
        setCommunes(list);

        // commune par défaut = commune du club (si présente), sinon ALL
        const defaultCommune = settings?.[0]?.commune_club;
        setSelectedCommune(
          list.includes(defaultCommune) ? defaultCommune : ALL
        );

        setRaw(Array.isArray(communityStats) ? communityStats : []);
      } catch (e) {
        console.error("Erreur lors du chargement des stats communes :", e);
        setRaw([]);
        setCommunes([]);
      } finally {
        alive && setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Re-groupe à chaque changement de raw via util fourni
  useEffect(() => {
    setGrouped(regrouperParTranchesDages(raw || []));
  }, [raw]);

  // Liste des tranches existantes triées selon TRANCHES_ORDER
  const tranches = useMemo(() => {
    const keys = Object.keys(grouped || {});
    return TRANCHES_ORDER.filter((t) => keys.includes(t));
  }, [grouped]);

  // Helpers de comptage selon filtres
  const applyGenre = (obj) => {
    // obj = { F: n, M: n }
    if (!obj) return 0;
    if (selectedGenre === ALL) return (obj.F || 0) + (obj.M || 0);
    if (selectedGenre === "Féminin") return obj.F || 0;
    if (selectedGenre === "Masculin") return obj.M || 0;
    return 0;
  };

  // Données "synthèse" (commune sélectionnée vs hors commune) pour chaque tranche
  const dataSynthese = useMemo(() => {
    if (!grouped) return [];
    const selectedTranches =
      selectedTranche === ALL ? tranches : [selectedTranche];

    return selectedTranches.map((t) => {
      const tr = grouped[t] || {}; // { [commune]: { F, M } }
      let sel = 0;
      let hors = 0;

      Object.entries(tr).forEach(([commune, FM]) => {
        const val = applyGenre(FM);
        if (selectedCommune === ALL)
          sel += val; // si "Toutes", tout va dans "Sélection"
        else if (commune === selectedCommune) sel += val; // commune choisie
        else hors += val; // hors commune
      });

      return { tranche: t, selection: sel, hors, total: sel + hors };
    });
  }, [grouped, selectedCommune, selectedGenre, selectedTranche, tranches]);

  // Totaux "synthèse"
  const totalsSynthese = useMemo(() => {
    return dataSynthese.reduce(
      (acc, r) => {
        acc.selection += r.selection;
        acc.hors += r.hors;
        acc.total += r.total;
        return acc;
      },
      { selection: 0, hors: 0, total: 0 }
    );
  }, [dataSynthese]);

  // Données "détail" (par commune) filtrées par tranche/gendre
  const dataDetail = useMemo(() => {
    if (!grouped) return [];
    const selectedTranches =
      selectedTranche === ALL ? tranches : [selectedTranche];

    // On reconstruit un tableau : [{ tranche, commune, valeur }, ...]
    const rows = [];
    selectedTranches.forEach((t) => {
      const tr = grouped[t] || {};
      Object.entries(tr).forEach(([commune, FM]) => {
        if (selectedCommune !== ALL && commune !== selectedCommune) return; // si commune spécifique
        rows.push({
          tranche: t,
          commune,
          value: applyGenre(FM),
        });
      });
    });

    // Tri par tranche (ordre défini) puis commune alphabétique
    return rows.sort((a, b) => {
      const i1 = TRANCHES_ORDER.indexOf(a.tranche);
      const i2 = TRANCHES_ORDER.indexOf(b.tranche);
      if (i1 !== i2) return i1 - i2;
      return String(a.commune).localeCompare(String(b.commune), "fr");
    });
  }, [grouped, selectedCommune, selectedGenre, selectedTranche, tranches]);

  const formatN = (x) => (x ?? 0).toLocaleString("fr-FR");

  if (loading) return <Skeleton />;

  const empty =
    (mode === "synthese" && dataSynthese.every((r) => r.total === 0)) ||
    (mode === "detail" && dataDetail.length === 0);

  return (
    <section
      className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur
  max-h-[85vh] overflow-y-auto overscroll-y-contain touch-pan-y
  sm:max-h-none sm:overflow-visible"
    >
      {" "}
      {/* En-tête + barre de style */}
      <div className="px-5 pt-5 sm:px-6">
        <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
          Statistiques des adhérents — communes, genre & tranches d’âge
        </h3>
        <div className="mt-2 h-1 w-24 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
      </div>
      {/* Filtres */}
      <div className="px-5 pb-0 sm:px-6 mt-4">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1 block">
              <FaFilter className="inline mr-1" /> Commune
            </label>
            <select
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              value={selectedCommune}
              onChange={(e) => setSelectedCommune(e.target.value)}
            >
              <option value={ALL}>Toutes les communes</option>
              {communes.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1 block">
              Genre
            </label>
            <select
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              value={selectedGenre}
              onChange={(e) => setSelectedGenre(e.target.value)}
            >
              <option value={ALL}>Tous</option>
              <option value="Féminin">Féminin</option>
              <option value="Masculin">Masculin</option>
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1 block">
              Tranche d’âge
            </label>
            <select
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              value={selectedTranche}
              onChange={(e) => setSelectedTranche(e.target.value)}
            >
              <option value={ALL}>Toutes</option>
              {tranches.map((t) => (
                <option key={t} value={t}>
                  {t} ans
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1 block">
              Mode d’affichage
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setMode("synthese")}
                className={`flex-1 inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 ring-1 transition ${
                  mode === "synthese"
                    ? "bg-violet-600 text-white ring-violet-400/40"
                    : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 ring-slate-300 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                }`}
              >
                <FaLayerGroup /> Synthèse
              </button>
              <button
                type="button"
                onClick={() => setMode("detail")}
                className={`flex-1 inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 ring-1 transition ${
                  mode === "detail"
                    ? "bg-violet-600 text-white ring-violet-400/40"
                    : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 ring-slate-300 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                }`}
              >
                <FaTable /> Détail
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Chips résumé */}
      <div className="px-5 sm:px-6 mt-4 mb-2">
        {mode === "synthese" ? (
          <div className="flex flex-wrap gap-2">
            <Chip
              label={
                selectedCommune === ALL
                  ? "Sélection : toutes communes"
                  : `Sélection : ${selectedCommune}`
              }
              value={formatN(totalsSynthese.selection)}
              tone="sky"
            />
            <Chip
              label={
                selectedCommune === ALL
                  ? "Hors sélection"
                  : `Hors ${selectedCommune}`
              }
              value={formatN(totalsSynthese.hors)}
              tone="fuchsia"
            />
            <Chip
              label="Total"
              value={formatN(totalsSynthese.total)}
              tone="emerald"
            />
          </div>
        ) : (
          <div className="text-sm text-slate-600 dark:text-slate-400">
            {selectedCommune === ALL
              ? "Affichage détaillé pour toutes les communes."
              : `Affichage détaillé pour la commune : ${selectedCommune}.`}
          </div>
        )}
      </div>
      {/* Tableau */}
      <div className="px-5 pb-6 sm:px-6">
        {empty ? (
          <div className="text-center py-12 text-slate-600 dark:text-slate-400">
            Aucune donnée à afficher avec les filtres actuels.
          </div>
        ) : mode === "synthese" ? (
          <div className="overflow-x-auto rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white dark:bg-slate-900">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50/70 dark:bg-slate-800/60">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-200 sticky left-0 bg-slate-50/70 dark:bg-slate-800/60">
                    Tranche d’âge
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-200">
                    {selectedCommune === ALL
                      ? "Sélection (toutes)"
                      : `Sélection (${selectedCommune})`}
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-200">
                    {selectedCommune === ALL
                      ? "Hors sélection"
                      : `Hors ${selectedCommune}`}
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-200">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {dataSynthese.map((row) => (
                  <tr
                    key={row.tranche}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/40"
                  >
                    <td className="px-4 py-2 sticky left-0 bg-white dark:bg-slate-900 font-medium">
                      {row.tranche} ans
                    </td>
                    <td className="px-4 py-2">{formatN(row.selection)}</td>
                    <td className="px-4 py-2">{formatN(row.hors)}</td>
                    <td className="px-4 py-2 font-semibold">
                      {formatN(row.total)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-slate-50/70 dark:bg-slate-800/60">
                  <td className="px-4 py-3 font-semibold">Total</td>
                  <td className="px-4 py-3 font-semibold">
                    {formatN(totalsSynthese.selection)}
                  </td>
                  <td className="px-4 py-3 font-semibold">
                    {formatN(totalsSynthese.hors)}
                  </td>
                  <td className="px-4 py-3 font-bold">
                    {formatN(totalsSynthese.total)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          // Détail
          <div className="overflow-x-auto rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white dark:bg-slate-900">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50/70 dark:bg-slate-800/60">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-200 sticky left-0 bg-slate-50/70 dark:bg-slate-800/60">
                    Tranche d’âge
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-200">
                    Commune
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-200">
                    Valeur
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {dataDetail.map((row, idx) => (
                  <tr
                    key={`${row.tranche}-${row.commune}-${idx}`}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/40"
                  >
                    <td className="px-4 py-2 sticky left-0 bg-white dark:bg-slate-900 font-medium">
                      {row.tranche} ans
                    </td>
                    <td className="px-4 py-2">{row.commune}</td>
                    <td className="px-4 py-2 font-semibold">
                      {formatN(row.value)}
                    </td>
                  </tr>
                ))}
                {dataDetail.length > 0 && (
                  <tr className="bg-slate-50/70 dark:bg-slate-800/60">
                    <td className="px-4 py-3 font-semibold">Total</td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400">
                      —
                    </td>
                    <td className="px-4 py-3 font-bold">
                      {formatN(
                        dataDetail.reduce((s, r) => s + (r.value || 0), 0)
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
